
	import { Component, Vue } from 'vue-property-decorator';
	import { Observer } from 'mobx-vue';
	import InsoleSVG from '@/components/InsoleSVG.vue';
	import EditSVG from '@/components/EditSVG.vue';
	import InfoModal from '@/components/InfoModal.vue';
	import ShoeSizeModal from '@/components/ShoeSizeModal.vue';
	import NotesModal from '@/components/modals/NotesModal.vue';
	import DualButtonModal from '@/components/modals/DualButtonModal.vue';
	import { OrthoticStyle } from '@/store/Products';
	import Loading from '@/components/Loading.vue';
	import { IReactionDisposer } from 'mobx';
	import EndViewCategory from '@/components/prescription/EndViewCategory.vue';
	import errorMessages from '@/assets/error-messages.json';
	import PrescriptionSummary from '@/components/new-prescription/prescription-summary/PrescriptionSummary.vue';
	import { ShoeSizeUnit } from '@/store/ShoeMeasurements';
	import UtilsStore from '@/store/utilsStore';
	import store from '@/store/mainStore';
	import { UiContextRecord } from '@/store/leroyUtils/uxContextDataDefinition';
	import { orthoticStyleToTitle } from '@/utils';
	import { orthoticStyleToImagePath } from '@/utils';

	@Observer
	@Component({
		components: {
			PrescriptionSummary,
			EndViewCategory,
			DualButtonModal,
			InsoleSVG,
			EditSVG,
			InfoModal,
			ShoeSizeModal,
			NotesModal,
			Loading
		},
		filters: {
			shoeUnitCase(str: ShoeSizeUnit) {
				const split = str.split(' ');
				if (split.length == 2) {
					return `${split[0]} ${UtilsStore.capitalize(split[1])}`;
				}
				return str;
			},
			capitalize(str: string) {
				return UtilsStore.capitalize(str);
			}
		}
	})
	export default class SummaryView extends Vue {
		private firstName: string = '';
		private noScans: boolean = false;
		// private ordering: boolean = false;
		private shoeSizeModal: boolean = false;
		private orderError: boolean = false;
		private somethingWentWrong = false;
		private subProps = ['Specify Values', 'Extrinsic Specify Values', 'Intrinsic Specify Values'];
		private dispose: IReactionDisposer | undefined;
		private clicked = false;
		private delay = true;
		private buttonKey: string = '0';
		private loadingKey: string = '1';
		private intervalForceUpdate = 0;
		openTrackingLink() {
			if (this.$state._patient.patientUIData.trackingLink) {
				window.open(this.$state._patient.patientUIData.trackingLink);
			}
		}

		ordering() {
			return this.$state.order.ordering;
		}

		hasPrescriptionInSessionStatus() {
			return this.$state._patient.patient?.sessionsStatuses?.some((i) => i?.status?.includes('PRESCRIPTION'));
		}

		getTrackingNumber(trackingLink: string) {
			return trackingLink.split('=').reverse()[0];
		}

		public async created(): Promise<void> {
			const scans = await this.$state.biometric.getUserScansList();
			if (scans && scans.length < 1) {
				this.noScans = true;
			}
		}

		public activated() {
			this.clicked = false;
			this.$state.order.ordering = false;
			this.delay = true;
			this.intervalForceUpdate = setInterval(() => {
				this.buttonKey = Date.now().toString();
				this.loadingKey = Date.now().toString() + 's';
				this.$forceUpdate();
			}, 3000);
			setTimeout(() => {
				this.delay = false;
				this.$forceUpdate(); //sometimes application is not reacitve. forceupdate makes recalculate page.
			}, 3420);
		}

		public deactivated() {
			this.delay = true; // When order is remade need to put this.delay to true to avoid active button in first ms of loaded summary page
			clearInterval(this.intervalForceUpdate);
		}

		async order() {
			try {
				this.$state.order.ordering = true;

				if (this.checkZebraID()) {
					return;
				}
				if (!(await this.$state._patient.checkDoctorValidity())) {
					this.$state._patient.patientUIData.assignedToDoctor = '';
					await this.$router.push({
						path: '/getting-started',
						params: { reassign: 'true' },
						query: { reassign: 'true' }
					});
					this.$state.utils.pushMessage(errorMessages.snackbars.reassign);
					this.$state.order.ordering = false;
					return;
				}
				const order = await this.$state.order.createOrder();
				if (order) {
					this.$state.order.order = { ...order };
					await this.$router.push('/end-view');
				}

				setTimeout(() => {
					this.$state.order.ordering = false;
					// this.$state._patient.hadOrder = true;
				}, 3000);
			} catch (e) {
				console.log(e);
				this.$state.order.ordering = false;
			}
		}

		async inspectDesign() {
			this.$state.leroy.SetScene('InsoleVisualizer');
			await new Promise((r) => setTimeout(r, 1000));
			this.$state.leroy.modalVisible = true;
		}

		checkZebraID() {
			if (UtilsStore.unifiedValidatorsForInput(this.$state._patient.patientUIData.zebraID)) {
				this.$state.order.ordering = false;
				this.$state.utils.pushMessage('Customer PO field is invalid: ' + errorMessages.snackbars.invalidCharacters);
				return true;
			}
		}
		async save() {
			if (this.checkZebraID()) {
				return;
			}
			await this.$state.prescription.savePrescription(true);
			this.$router.push('patients');
			location.reload();
		}

		async done() {
			if (this.$router.currentRoute.name !== 'Patients') {
				await this.$router.push('patients');
			}
			location.reload();
		}

		async newOrder() {
			if (this.clicked) return;
			this.clicked = true;
			const patient = await this.$state.order.reorder();
			await this.$router.push('/summary?id=' + patient?.user?.userID);
		}

		back(category: string = '') {
			this.$router.push('prescription#' + category);
		}

		validateInput(e) {
			const res = UtilsStore.unifiedValidatorsForInput(e.srcElement._value);
			if (res) {
				this.$state.utils.pushMessage(errorMessages.snackbars.invalidCharacters);
				this.$state._patient.patientUIData.zebraID = UtilsStore.removeInvalidCharactersForInput(this.$state._patient.patientUIData.zebraID);
			}
		}

		mapOrthoticStyleToTitle(orthoticStyle) {
			return orthoticStyleToTitle(orthoticStyle);
		}

		mapOrthoticStyleToImagePath(orthoticStyle) {
			return orthoticStyleToImagePath(orthoticStyle);
		}
	}
